import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {ListEmptyState} from '@/components/section-card'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  IPS_PAGE_SIZE,
  useCorregirIps,
  useCrearIps,
  useDeclararIps,
  useIpsList,
  type Ips,
} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateEmail, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'
import {SearchInput} from '@/components/ui/search-input'

const LICENCIA_TONE: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  vigente: 'green',
  por_vencer: 'amber',
  vencida: 'red',
  sin_licencia: 'neutral',
}

/** El correo es el único destino al que Kairos manda datos de un trabajador (Ley 1581/2012): lleva
 * validador porque uno mal escrito no rebota, llega a otra persona. */
function IpsDialog({
  open,
  onOpenChange,
  businessId,
  ips,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  ips: Ips | null
}) {
  const {t} = useTranslation()
  const crear = useCrearIps(businessId)
  const corregir = useCorregirIps(businessId)
  const [nombre, setNombre] = useState('')
  const [correo, setCorreo] = useState('')
  const [licencia, setLicencia] = useState({numero: '', titular: '', entidad: '', venceEl: ''})
  const [errores, setErrores] = useState<{nombre?: string; correo?: string}>({})

  useEffect(() => {
    if (open) {
      setNombre(ips?.name ?? '')
      setCorreo(ips?.correo_institucional ?? '')
      setLicencia({
        numero: ips?.licencia_numero ?? '',
        titular: ips?.licencia_titular ?? '',
        entidad: ips?.licencia_entidad ?? '',
        venceEl: ips?.licencia_vence_el ?? '',
      })
      setErrores({})
      crear.reset()
      corregir.reset()
    }
    // Solo al abrir: re-sincronizar en cada render pisaría lo que la persona escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ips])

  function blurNombre(e: FocusEvent<HTMLInputElement>) {
    setErrores((prev) => ({...prev, nombre: validateRequired(e.target.value) ?? undefined}))
  }

  function blurCorreo(e: FocusEvent<HTMLInputElement>) {
    setErrores((prev) => ({...prev, correo: validateEmail(e.target.value) ?? undefined}))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const hallazgos = {
      nombre: validateRequired(nombre) ?? undefined,
      correo: validateEmail(correo) ?? undefined,
    }
    setErrores(hallazgos)
    if (hallazgos.nombre || hallazgos.correo) return
    const cerrar = {onSuccess: () => onOpenChange(false)}
    if (ips) {
      corregir.mutate(
        {
          ipsId: ips.id,
          name: nombre.trim(),
          correoInstitucional: correo.trim(),
          licenciaNumero: licencia.numero.trim(),
          licenciaTitular: licencia.titular.trim(),
          licenciaEntidad: licencia.entidad.trim(),
          licenciaVenceEl: licencia.venceEl,
        },
        cerrar
      )
    } else {
      crear.mutate({name: nombre.trim(), correoInstitucional: correo.trim()}, cerrar)
    }
  }

  const enVuelo = ips ? corregir : crear
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(ips ? 'ips.dialogo.editar' : 'ips.dialogo.registrar')}</DialogTitle>
          <DialogDescription>{t('ips.dialogo.ayuda')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="ips-nombre">{t('ips.campos.nombre')}</Label>
            <Input
              maxLength={tope('NOTA')}
              id="ips-nombre"
              data-testid="ips-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onBlur={blurNombre}
              aria-invalid={!!errores.nombre}
            />
            <FieldError error={errores.nombre} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ips-correo">{t('ips.campos.correo')}</Label>
            <Input
              maxLength={tope('MAIL')}
              id="ips-correo"
              data-testid="ips-correo"
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              onBlur={blurCorreo}
              aria-invalid={!!errores.correo}
            />
            <FieldError error={errores.correo} />
            <p className="text-xs text-muted-foreground">{t('ips.campos.correoAyuda')}</p>
          </div>
          {/* Solo al corregir: al registrar la IPS aún no hay dónde colgar el soporte de la licencia. */}
          {ips && (
            <div className="space-y-2" data-testid="ips-licencia-campos">
              <Label htmlFor="ips-licencia-numero">{t('ips.campos.licencia')}</Label>
              <p className="text-xs text-muted-foreground">{t('ips.campos.licenciaAyuda')}</p>
              <Input
                maxLength={tope('CODE')}
                id="ips-licencia-numero"
                data-testid="ips-licencia-numero"
                placeholder={t('ips.campos.licenciaNumero')}
                value={licencia.numero}
                onChange={(e) => setLicencia({...licencia, numero: e.target.value})}
              />
              <Input
                maxLength={tope('CODE')}
                data-testid="ips-licencia-titular"
                placeholder={t('ips.campos.licenciaTitular')}
                value={licencia.titular}
                onChange={(e) => setLicencia({...licencia, titular: e.target.value})}
              />
              <Input
                maxLength={tope('CODE')}
                data-testid="ips-licencia-entidad"
                placeholder={t('ips.campos.licenciaEntidad')}
                value={licencia.entidad}
                onChange={(e) => setLicencia({...licencia, entidad: e.target.value})}
              />
              <Input
                type="date"
                data-testid="ips-licencia-vence"
                aria-label={t('ips.campos.licenciaVence')}
                value={licencia.venceEl}
                onChange={(e) => setLicencia({...licencia, venceEl: e.target.value})}
              />
            </div>
          )}
          <FormDialogFooter
            error={enVuelo.isError ? translateApiError(enVuelo.error) : null}
            pending={enVuelo.isPending}
            submitLabel={t('ips.dialogo.guardar')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Kairos no verifica la licencia de un tercero: muestra quién atestó y cuándo, y una licencia vencida
 * cambia la etiqueta pero no bloquea nada (E1 §10). */
export function IpsPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const table = useTableState('name')
  const lista = useIpsList(business ? businessId : undefined, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
  })
  const declarar = useDeclararIps(businessId!)
  const [dialogo, setDialogo] = useState(false)
  const [editando, setEditando] = useState<Ips | null>(null)
  const [atestando, setAtestando] = useState<Ips | null>(null)

  if (guard) return guard

  // Los endpoints exigen `require_paid_business`: sin esta puerta un tenant gratuito recibe 402 sin mensaje.
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage
        feature={t('plans.feature.solicitudesIps')}
        businessId={businessId}
        businessName={business!.name}
      />
    )
  }

  if (lista.isError) {
    return (
      <div className="space-y-3">
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="ips-error-carga">
          {t('ips.errorCarga')}
        </p>
        <Button size="sm" variant="outline" onClick={() => void lista.refetch()}>
          {t('ips.reintentar')}
        </Button>
      </div>
    )
  }

  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  const razonBloqueo = godReadOnly
    ? t(capabilities.sgsst_firmar ? 'ips.bloqueoSeguro' : 'ips.bloqueoModoDios')
    : capabilities.firmar_bloqueado_por

  const columns: Column<Ips>[] = [
    {
      key: 'name',
      header: t('ips.columnas.nombre'),
      sortable: true,
      width: 'w-1/4',
      cell: (fila) => <span className="text-sm font-medium">{fila.name}</span>,
    },
    {
      key: 'correo',
      header: t('ips.columnas.correo'),
      width: 'w-1/4',
      cell: (fila) => <span className="font-mono text-xs">{fila.correo_institucional}</span>,
    },
    {
      key: 'licencia',
      header: t('ips.columnas.licencia'),
      width: 'w-44',
      cell: (fila) => (
        <CeldaEstado
          chip={
            <Tag tone={LICENCIA_TONE[fila.licencia_estado]} data-testid={`ips-licencia-${fila.id}`}>
              {t(`ips.licenciaChip.${fila.licencia_estado}`)}
            </Tag>
          }
          meta={
            fila.licencia_vence_el ? (
              <span className="font-mono" data-testid={`ips-vence-${fila.id}`}>
                {formatDateOnly(fila.licencia_vence_el)}
              </span>
            ) : undefined
          }
          porque={t(`ips.licencia.${fila.licencia_estado}`, {
            fecha: fila.licencia_vence_el ? formatDateOnly(fila.licencia_vence_el) : '',
            dias: fila.licencia_dias ?? 0,
          })}
        />
      ),
    },
    {
      key: 'declaracion',
      header: t('ips.columnas.declaracion'),
      cell: (fila) => (
        <span className="text-xs text-muted-foreground" data-testid={`ips-declaracion-${fila.id}`}>
          {fila.declaracion_por
            ? t('ips.declaracion.hecha', {
                nombre: fila.declaracion_por,
                fecha: formatDateOnly(fila.declaracion_en!),
              }) +
              (fila.declaracion_vigente_hasta
                ? t('ips.declaracion.vigenteHasta', {hasta: formatDateOnly(fila.declaracion_vigente_hasta)})
                : t('ips.declaracion.sinVigencia'))
            : t('ips.declaracion.pendiente')}
        </span>
      ),
    },
    {
      key: 'accion',
      header: t('ips.columnas.accion'),
      width: 'w-32',
      // Sin esto, atestiguar dispara también el onRowClick y abre el formulario de edición encima.
      stopClick: true,
      cell: (fila) => (
        <Button
          size="sm"
          variant="outline"
          data-testid={`ips-atestiguar-${fila.id}`}
          disabled={!puedeFirmar || declarar.isPending}
          onClick={() => setAtestando(fila)}>
          {t('ips.atestiguar')}
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="ips-page">
      <PageHeading
        title={t('ips.title')}
        subtitle={business!.name}
        ayuda={
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('ips.title')})} testId="ips-intro">
            {t('ips.intro')}
          </HelpIcon>
        }
        accion={
          puedeFirmar && (
            <Button
              size="sm"
              data-testid="ips-registrar"
              onClick={() => {
                setEditando(null)
                setDialogo(true)
              }}>
              {t('ips.registrar')}
            </Button>
          )
        }
      />

      {!puedeFirmar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="ips-bloqueo">
          {razonBloqueo}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={table.searchInput}
          onChange={table.setSearchInput}
          placeholder={t('ips.buscar')}
          data-testid="ips-buscar"
        />
        <div className="flex flex-wrap items-center gap-2">
          {/* El sidebar solo enlaza esta pantalla: sin este botón «Solicitudes a la IPS» no es alcanzable. */}
          <Button asChild size="sm" variant="outline" data-testid="ips-a-solicitudes">
            <Link to={`/businesses/${businessId}/solicitudes-ips`}>{t('ips.aSolicitudes')}</Link>
          </Button>
        </div>
      </div>

      <div className="surface rounded-xl p-1.5">
        <DataTable
          columns={columns}
          rows={lista.data?.items ?? []}
          rowKey={(fila) => fila.id}
          sortBy={table.sortBy}
          sortDir={table.sortDir}
          onSort={table.toggleSort}
          onRowClick={
            puedeFirmar
              ? (fila) => {
                  setEditando(fila)
                  setDialogo(true)
                }
              : undefined
          }
          emptyState={<ListEmptyState page title={t('ips.vacio.title')} body={t('ips.vacio.body')} />}
        />
        {(lista.data?.total ?? 0) > 0 && (
          <PaginationBar
            page={table.page}
            pageSize={IPS_PAGE_SIZE}
            total={lista.data!.total}
            onPageChange={table.setPage}
          />
        )}
      </div>

      <IpsDialog open={dialogo} onOpenChange={setDialogo} businessId={businessId!} ips={editando} />

      {/* Atestar tiene consecuencia legal: el modal imprime el texto de la declaración que se firma. */}
      <ConfirmDialog
        open={atestando !== null}
        onOpenChange={(abierto) => !abierto && setAtestando(null)}
        title={t('ips.atestiguarDialogo.title', {ips: atestando?.name ?? ''})}
        description={
          t('ips.atestiguarDialogo.cuerpo') +
          (atestando?.licencia_vence_el
            ? t('ips.atestiguarDialogo.vence', {fecha: formatDateOnly(atestando.licencia_vence_el)})
            : t('ips.atestiguarDialogo.sinLicencia'))
        }
        porque={{
          label: t('ips.atestiguarDialogo.porqueLabel'),
          testId: 'ips-atestiguar-porque',
          contenidoTestId: 'ips-atestiguar-porque-contenido',
          contenido: <p>{t('ips.atestiguarDialogo.porqueBody')}</p>,
        }}
        confirmLabel={t('ips.atestiguarDialogo.confirmar')}
        cancelLabel={t('common.cancel')}
        pending={declarar.isPending}
        onConfirm={() => atestando && declarar.mutate(atestando.id, {onSuccess: () => setAtestando(null)})}
      />
    </div>
  )
}
